'use client'

import { createContext, ReactNode, useContext, useEffect, useState } from 'react'

import { clearPavedClientAuth, PVD_ME_STORAGE_KEY } from '@/app/utils/clearPavedClientAuth'

export interface UserMenuItem {
  name: string
  icon: string
  link: string
  reload?: boolean
}

export interface UserData {
  ro_id: number
  uuid: string
  first_name: string
  last_name: string
  company_name: string
  company_id: number
  user_type: 'advertiser' | 'publisher' | string
  email: string
  stripe_id?: string
  has_cpa_beta_access: boolean
  has_day_parting_beta_access: boolean
  has_active_pixels: boolean
  created_at: number
  show_gdpr_consent: boolean
  status: string
  has_booker_access: boolean
  has_ad_network_access: boolean
  has_marketplace_access: boolean
  menu: UserMenuItem[]
  messages: {
    notice: string | null
    alert: string | null
    error: string | null
  }
  segments: number[]
}

function isValidUserData(data: unknown): data is UserData {
  if (!data || typeof data !== 'object') return false
  const user = data as Record<string, unknown>
  return (
    typeof user.ro_id === 'number' &&
    typeof user.uuid === 'string' &&
    typeof user.email === 'string' &&
    typeof user.first_name === 'string' &&
    typeof user.last_name === 'string' &&
    Array.isArray(user.menu)
  )
}

interface UserContextType {
  user: UserData | null
  isLoading: boolean
  setUser: (user: UserData | null) => void
  logout: () => void
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<UserData | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const getCookie = (name: string): string | undefined =>
      document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${name}=`))
        ?.split('=')[1]

    // Get bearer token from cookie directly, or decode from rasuwa (set by Rails after SSO)
    const getBearerToken = (): string | undefined => {
      const bearer = getCookie('bearer')
      if (bearer) return bearer

      const rasuwa = getCookie('rasuwa')
      if (!rasuwa) return undefined

      try {
        const { t: accessToken } = JSON.parse(atob(decodeURIComponent(rasuwa)))
        return accessToken
      } catch {
        return undefined
      }
    }

    const loadUserData = async () => {
      try {
        const isProduction = window.location.hostname === 'www.paved.com'
        if (!isProduction) {
          // Use localStorage fallback on non-production domains to avoid CORS errors
          const storedData = localStorage.getItem(PVD_ME_STORAGE_KEY)
          if (storedData) {
            setUserState(JSON.parse(storedData))
          }
          return
        }

        const bearerToken = getBearerToken()

        if (bearerToken) {
          const response = await fetch('https://www.paved.com/api/v1/users/me', {
            headers: {
              Authorization: `Bearer ${bearerToken}`,
            },
          })

          if (response.ok) {
            try {
              const userData = await response.json()

              if (isValidUserData(userData)) {
                if (userData.user_type == null) {
                  window.location.href = '/users/onboarding/account_details'
                  return
                }
                setUserState(userData)
                localStorage.setItem(PVD_ME_STORAGE_KEY, JSON.stringify(userData))
              }
            } catch (jsonError) {
              console.error('Failed to parse user data JSON:', jsonError)
            }
          }
        } else {
          // No auth token on production — clear stale localStorage data
          localStorage.removeItem(PVD_ME_STORAGE_KEY)
        }
      } catch (error) {
        console.error('Failed to load user data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadUserData()
  }, [])

  const setUser = (userData: UserData | null) => {
    setUserState(userData)
    if (userData) {
      localStorage.setItem(PVD_ME_STORAGE_KEY, JSON.stringify(userData))
    } else {
      localStorage.removeItem(PVD_ME_STORAGE_KEY)
    }
  }

  const logout = () => {
    clearPavedClientAuth()
    setUserState(null)
  }

  return (
    <UserContext.Provider value={{ user, isLoading, setUser, logout }}>
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}
